import React, { useState } from 'react';
import '../styles/LiveDataFeed.css';
import Sidebar from '../components/layout/Sidebar';
import Header from '../components/layout/Header';
import { useAuth, useNews } from '../hooks';

const LiveDataFeed = () => {
  const { currentUser, logout } = useAuth();
  const { news, loadingNews, hasMoreNews, latestNewsDate, loadMoreNews } = useNews();
  const [showUserMenu, setShowUserMenu] = useState(false);

  return (
    <>
      <Header
        currentUser={currentUser}
        showUserMenu={showUserMenu}
        setShowUserMenu={setShowUserMenu}
        onLogout={logout}
      />

      <div className="app">
        <Sidebar />

        <div className="main-content">
          <div className="content-area">

          {/* Hero Section - Bloomberg Style */}
          <div className="feed-hero">
            <div className="feed-hero-content">
              <div className="feed-hero-label">LIVE INTELLIGENCE</div>
              <h1 className="feed-hero-title">Real-Time Regulatory Data Feed</h1>
              <p className="feed-hero-description">
                Comprehensive coverage of EU financial services regulations. Automated monitoring across EUR-Lex, ESMA, EBA, ECB, and national authorities with instant notifications.
              </p>
              <div className="feed-stats">
                <div className="feed-stat">
                  <div className="feed-stat-value">{news.length}+</div>
                  <div className="feed-stat-label">Updates Today</div>
                </div>
                <div className="feed-stat">
                  <div className="feed-stat-value">24/7</div>
                  <div className="feed-stat-label">Live Monitoring</div>
                </div>
                <div className="feed-stat">
                  <div className="feed-stat-value">5+</div>
                  <div className="feed-stat-label">Data Sources</div>
                </div>
              </div>
            </div>
            <div className="feed-hero-live">
              <div className="live-indicator">
                <span className="live-pulse"></span>
                <span className="live-text">LIVE</span>
              </div>
              <div className="live-timestamp">
                {new Date().toLocaleTimeString('en-GB', {
                  hour: '2-digit',
                  minute: '2-digit',
                  timeZone: 'Europe/Brussels',
                  timeZoneName: 'short'
                })} • NOW
              </div>
            </div>
          </div>

          {/* News Stream */}
          <div className="feed-stream">
            <div className="stream-header">
              <h2 className="stream-title">Latest Updates</h2>
              <div className="stream-count">{news.length} articles</div>
            </div>

            <div className="stream-grid">
              {loadingNews && news.length === 0 ? (
                <div className="stream-loading">
                  <div className="loading-spinner"></div>
                  <p>Loading live feed...</p>
                </div>
              ) : news.length === 0 ? (
                <div className="stream-empty">
                  <p>No updates available</p>
                </div>
              ) : (
                news.map((item, index) => {
                  const dateValue = item.pubdate || item.pubDate || item.last_pubdate;
                  const pubDate = new Date(dateValue);
                  const timeAgo = getTimeAgo(pubDate);

                  return (
                    <article key={item.id} className="feed-card" style={{animationDelay: `${index * 0.05}s`}}>
                      <div className="feed-card-header">
                        <span className="feed-card-category">
                          {item.source?.toUpperCase() || 'REGULATORY'}
                        </span>
                        <span className="feed-card-time">{timeAgo}</span>
                      </div>

                      <h3 className="feed-card-title">{item.title}</h3>

                      <div className="feed-card-body">
                        {item.description ? (
                          <p>{truncateText(item.description, 180)}</p>
                        ) : item.source === 'esma_library' ? (
                          <div className="feed-card-meta">
                            {item.reference && <div><strong>Ref:</strong> {item.reference}</div>}
                            {item.type && <div><strong>Type:</strong> {item.type}</div>}
                          </div>
                        ) : (
                          <p>Details available in full document</p>
                        )}
                      </div>

                      {item.link && (
                        <a href={item.link} target="_blank" rel="noopener noreferrer" className="feed-card-link">
                          Read full article
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M7 17L17 7M17 7H7M17 7V17"/>
                          </svg>
                        </a>
                      )}
                    </article>
                  );
                })
              )}
            </div>

            {hasMoreNews && (
              <div className="stream-footer">
                <button
                  className="load-more-btn"
                  onClick={loadMoreNews}
                  disabled={loadingNews}
                >
                  {loadingNews ? 'Loading...' : 'Load More Updates'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

// Helper functions
const getTimeAgo = (date) => {
  const seconds = Math.floor((new Date() - date) / 1000);
  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const truncateText = (text, maxLength) => {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength).trim() + '...';
};

export default LiveDataFeed;
